import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { normalizeTestId } from "@/lib/test-helper"
import { cn } from "@/lib/utils"

interface SingleSelectBase<Option> {
  readonly options: readonly Option[]
}

interface SingleSelectCommonProps<Option> {
  label: string
  value?: Option | null
  isSearchable?: boolean
  "data-testid"?: string
  isOptionSelected?: (option: Option, selectedValue?: Option | null) => boolean
  getOptionLabel: (option: Option) => string
  getOptionValue: (option: Option) => string
  onChange?: (value: Option | null) => void
  onFilter?: (value: string, search: string) => 0 | 1
}

export const SingleSelect = <Option,>(props: SingleSelectBase<Option> & SingleSelectCommonProps<Option>) => {
  const { t } = useTranslation()

  const {
    value,
    options,
    label,
    isSearchable,
    "data-testid": dataTestId,
    isOptionSelected,
    onChange,
    getOptionValue,
    getOptionLabel,
    onFilter,
  } = props

  return (
    <DropdownMenu modal>
      <DropdownMenuTrigger
        asChild
        className="aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden hover:cursor-pointer">
        <Chip
          data-testid={dataTestId && normalizeTestId(dataTestId, "-filter-trigger")}
          className="h-8 gap-0.5"
          checked={!!value}
          type="button">
          <div className={`overflow-hidden transition-all duration-200 ${value ? "w-5" : "w-0"}`}>
            <Check size={20} />
          </div>
          <span className="px-1.5">{label}</span>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        data-testid={dataTestId && normalizeTestId(dataTestId, "-filter-content")}
        className="absolute -top-1 grid gap-2 rounded-md border-none p-2 leading-5">
        <Command
          className="bg-surface *:[[cmdk-input-wrapper]]:border-b *:[[cmdk-input-wrapper]]:p-2"
          filter={onFilter}>
          {isSearchable ? <CommandInput placeholder={t("common.search")} className="bg-surface pr-12" /> : null}
          <CommandList className="bg-surface py-2">
            <CommandEmpty className="px-2 py-1.5 text-sm">{t("common.nothingFound")}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const optionValue = getOptionValue(option)
                const label = getOptionLabel(option)
                const isSelected = isOptionSelected?.(option, value)

                return (
                  <CommandItem
                    key={optionValue}
                    value={optionValue}
                    className={cn(
                      "mt-0.75 cursor-pointer flex-wrap gap-x-1 rounded-md",
                      isSelected &&
                        "bg-primary-surface! text-on-primary-surface! hover:bg-primary-surface-hovered! hover:cursor-pointer",
                    )}
                    data-testid={dataTestId && normalizeTestId(dataTestId, "-option")}
                    onSelect={() => onChange?.(option)}>
                    {label}
                    {isSelected && <Check />}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        <DropdownMenuSeparator className="bg-border -mx-2 my-0" />
        <div className="flex justify-end">
          <Button
            onClick={() => onChange?.(null)}
            disabled={!value}
            data-testid={dataTestId && normalizeTestId(dataTestId, "-filter-reset")}
            variant="tertiary"
            className="h-9 rounded-full px-2 py-0 leading-5 font-medium">
            {t("common.button.reset")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
